import React, { useEffect, useMemo, useState } from 'react';
import LiveWorkouts from './LiveWorkouts';
import {
  GatewayContext,
  GatewayContextValue,
  StationPresence,
  LiveSessionState,
} from '../../providers/gatewayProvider';
import {
  SessionDataContext,
  SessionDataContextProps,
  useSessionData,
} from '../../providers/sessionDataProvider';
import {
  UserActionContext,
  UserActionContextProps,
  useUserAction,
} from '../../providers/userActionProvider';
import { AutomaticMetricsType, Status } from '../../constants';
import type {
  Exercise,
  Gym,
  Group,
  Member,
  TrainingRoutineSession,
} from '../../types';
import type { LiveRepFrame, RepMetric } from '../../types/automatic';
import type { SessionMeta } from '../../types/gateway';

const DEMO_GYM_ID = 'demo-gym';
const DEMO_GROUP_ID = 'demo-group';
const MEMBERS_PER_STATION = 3;
const ACTIVE_STATION_COUNT = 3;
const TOTAL_STATIONS = 20;
const TOTAL_MEMBERS = MEMBERS_PER_STATION * TOTAL_STATIONS;

type TempoProfile = 'balanced' | 'fast' | 'slow';

interface DemoMemberPlan {
  memberId: string;
  exerciseId: string;
  baseWeight: number;
  weightVariance: number;
  repTargetRange: [number, number];
  tempoProfile: TempoProfile;
  concentricRange: [number, number];
  eccentricRange: [number, number];
}

interface DemoStationPlan {
  stationId: string;
  stationNumber: number;
  status: StationPresence['status'];
  plans: DemoMemberPlan[];
}

interface StationRuntime {
  config: DemoStationPlan;
  currentPlanIndex: number;
  sessionId: string;
  meta: SessionMeta;
  frames: LiveRepFrame[];
  repsCompleted: number;
  targetReps: number;
  lastTs: number;
}

const seededMemberProfiles = [
  { id: 'athlete-1', firstName: 'Alex', lastName: 'Rivers' },
  { id: 'athlete-2', firstName: 'Brooke', lastName: 'Chen' },
  { id: 'athlete-3', firstName: 'Carter', lastName: 'Diaz' },
  { id: 'athlete-4', firstName: 'Dani', lastName: 'Alvarez' },
  { id: 'athlete-5', firstName: 'Evan', lastName: 'Kim' },
  { id: 'athlete-6', firstName: 'Frankie', lastName: 'Singh' },
  { id: 'athlete-7', firstName: 'Gia', lastName: 'Patel' },
  { id: 'athlete-8', firstName: 'Hayden', lastName: 'Brooks' },
  { id: 'athlete-9', firstName: 'Ivy', lastName: 'Nakamura' },
];

const additionalFirstNames = [
  'Jaden',
  'Kai',
  'Lena',
  'Mason',
  'Nia',
  'Owen',
  'Piper',
  'Quinn',
  'Riley',
  'Sage',
  'Theo',
  'Uma',
  'Vera',
  'Wes',
  'Xavier',
  'Yara',
  'Zane',
  'Avery',
  'Blake',
  'Colt',
];

const additionalLastNames = [
  'Garcia',
  'Lopez',
  'Nguyen',
  'Ortiz',
  'Park',
  'Quintero',
  'Reyes',
  'Sharma',
  'Tanaka',
  'Vasquez',
  'Williams',
  'Young',
  'Zimmerman',
];

const additionalProfilesCount = TOTAL_MEMBERS - seededMemberProfiles.length;

const generatedMemberProfiles = Array.from(
  { length: additionalProfilesCount },
  (_, idx) => {
    const idNumber = seededMemberProfiles.length + idx + 1;
    const first =
      additionalFirstNames[idx % additionalFirstNames.length] ??
      `Demo${idx + 1}`;
    const last =
      additionalLastNames[
        (idx + Math.floor(idx / additionalFirstNames.length)) %
          additionalLastNames.length
      ] || 'Member';
    return {
      id: `athlete-${idNumber}`,
      firstName: first,
      lastName: last,
    };
  },
);

const demoMemberProfiles = [
  ...seededMemberProfiles,
  ...generatedMemberProfiles,
];

const buildDemoMember = (profile: {
  id: string;
  firstName: string;
  lastName: string;
}): Member => ({
  id: profile.id,
  email:
    `${profile.firstName}.${profile.lastName}@demo.motivision`.toLowerCase(),
  firstName: profile.firstName,
  lastName: profile.lastName,
  name: `${profile.firstName} ${profile.lastName}`,
  phoneNumber: '555-0100',
  trainerIds: ['demo-trainer'],
  gender: null,
  dateOfBirth: '1995-01-01',
  height: 70,
  weeklyWorkouts: 3,
  fitnessGoal: 'Performance',
  subscriptions: [],
  weighIn: [],
  targetWeight: 0,
  preferences: {},
  gymIds: [DEMO_GYM_ID],
  pin: '0000',
  isActive: true,
  signedDate: new Date(),
  hasAgreedToTerms: true,
  hasAgreedToWeightIn: true,
  leadStatus: 'active',
  createdDate: new Date(),
  records: undefined,
  program: undefined,
  groupIds: [DEMO_GROUP_ID],
});

const demoMembers = demoMemberProfiles.reduce<Record<string, Member>>(
  (acc, profile) => {
    acc[profile.id] = buildDemoMember(profile);
    return acc;
  },
  {},
);

const demoExercises: Exercise[] = [
  {
    id: 'exercise-hex-bar',
    name: 'Trap Bar Deadlift',
    videoUrl: '',
    imageUrl: '',
    primaryMuscleGroupIds: [],
    secondaryMuscleGroupIds: [],
    preferredStationIds: [],
    movementPattern: 'hinge',
    modality: 'strength',
    planeOfMotion: 'sagittal',
    isUnilateral: false,
    isWeightsPerSide: false,
  },
  {
    id: 'exercise-bench',
    name: 'Tempo Bench Press',
    videoUrl: '',
    imageUrl: '',
    primaryMuscleGroupIds: [],
    secondaryMuscleGroupIds: [],
    preferredStationIds: [],
    movementPattern: 'push',
    modality: 'strength',
    planeOfMotion: 'transverse',
    isUnilateral: false,
    isWeightsPerSide: false,
  },
  {
    id: 'exercise-row',
    name: 'Single Arm Row',
    videoUrl: '',
    imageUrl: '',
    primaryMuscleGroupIds: [],
    secondaryMuscleGroupIds: [],
    preferredStationIds: [],
    movementPattern: 'pull',
    modality: 'strength',
    planeOfMotion: 'sagittal',
    isUnilateral: true,
    isWeightsPerSide: false,
  },
  {
    id: 'exercise-lunge',
    name: 'Rear Foot Elevated Lunge',
    videoUrl: '',
    imageUrl: '',
    primaryMuscleGroupIds: [],
    secondaryMuscleGroupIds: [],
    preferredStationIds: [],
    movementPattern: 'lunge',
    modality: 'strength',
    planeOfMotion: 'sagittal',
    isUnilateral: true,
    isWeightsPerSide: false,
  },
  {
    id: 'exercise-press',
    name: 'Single Arm Landmine Press',
    videoUrl: '',
    imageUrl: '',
    primaryMuscleGroupIds: [],
    secondaryMuscleGroupIds: [],
    preferredStationIds: [],
    movementPattern: 'press',
    modality: 'strength',
    planeOfMotion: 'frontal',
    isUnilateral: true,
    isWeightsPerSide: false,
  },
];

const demoExercisesMap = demoExercises.reduce<Record<string, Exercise>>(
  (acc, exercise) => {
    acc[exercise.id] = exercise;
    return acc;
  },
  {},
);

const clampReps = (value: number) => Math.max(8, Math.min(12, value));

const activeStationPlans: DemoStationPlan[] = [
  {
    stationId: 'station-01',
    stationNumber: 1,
    status: 'UP',
    plans: [
      {
        memberId: 'athlete-1',
        exerciseId: 'exercise-hex-bar',
        baseWeight: 225,
        weightVariance: 20,
        repTargetRange: [8, 10],
        tempoProfile: 'balanced',
        concentricRange: [1.1, 1.8],
        eccentricRange: [1.4, 2.2],
      },
      {
        memberId: 'athlete-2',
        exerciseId: 'exercise-hex-bar',
        baseWeight: 205,
        weightVariance: 15,
        repTargetRange: [8, 10],
        tempoProfile: 'fast',
        concentricRange: [0.9, 1.4],
        eccentricRange: [1.2, 1.9],
      },
      {
        memberId: 'athlete-3',
        exerciseId: 'exercise-hex-bar',
        baseWeight: 245,
        weightVariance: 25,
        repTargetRange: [8, 10],
        tempoProfile: 'slow',
        concentricRange: [1.3, 2.0],
        eccentricRange: [1.6, 2.4],
      },
    ],
  },
  {
    stationId: 'station-02',
    stationNumber: 2,
    status: 'UP',
    plans: [
      {
        memberId: 'athlete-4',
        exerciseId: 'exercise-bench',
        baseWeight: 145,
        weightVariance: 10,
        repTargetRange: [8, 12],
        tempoProfile: 'balanced',
        concentricRange: [1.0, 1.6],
        eccentricRange: [1.4, 2.1],
      },
      {
        memberId: 'athlete-5',
        exerciseId: 'exercise-bench',
        baseWeight: 120,
        weightVariance: 12,
        repTargetRange: [9, 12],
        tempoProfile: 'slow',
        concentricRange: [1.1, 1.8],
        eccentricRange: [1.6, 2.3],
      },
      {
        memberId: 'athlete-6',
        exerciseId: 'exercise-press',
        baseWeight: 75,
        weightVariance: 8,
        repTargetRange: [8, 10],
        tempoProfile: 'fast',
        concentricRange: [0.8, 1.2],
        eccentricRange: [1.2, 1.8],
      },
    ],
  },
  {
    stationId: 'station-03',
    stationNumber: 3,
    status: 'DEGRADED',
    plans: [
      {
        memberId: 'athlete-7',
        exerciseId: 'exercise-row',
        baseWeight: 70,
        weightVariance: 12,
        repTargetRange: [9, 12],
        tempoProfile: 'balanced',
        concentricRange: [1.2, 1.9],
        eccentricRange: [1.6, 2.3],
      },
      {
        memberId: 'athlete-8',
        exerciseId: 'exercise-lunge',
        baseWeight: 55,
        weightVariance: 10,
        repTargetRange: [8, 12],
        tempoProfile: 'slow',
        concentricRange: [1.4, 2.2],
        eccentricRange: [1.8, 2.6],
      },
      {
        memberId: 'athlete-9',
        exerciseId: 'exercise-row',
        baseWeight: 60,
        weightVariance: 9,
        repTargetRange: [8, 11],
        tempoProfile: 'fast',
        concentricRange: [1.0, 1.6],
        eccentricRange: [1.3, 2.0],
      },
    ],
  },
];

const tempoCycle: TempoProfile[] = ['balanced', 'fast', 'slow'];
const statusCycle: StationPresence['status'][] = [
  'UP',
  'DEGRADED',
  'STARTING',
  'UP',
  'CAMERA_ISSUE',
  'UNKNOWN',
  'UP',
  'DOWN',
];

const createStaticStationPlans = (): DemoStationPlan[] => {
  const staticStations: DemoStationPlan[] = [];
  const staticCount = TOTAL_STATIONS - ACTIVE_STATION_COUNT;
  const memberOffset = ACTIVE_STATION_COUNT * MEMBERS_PER_STATION;

  for (let i = 0; i < staticCount; i++) {
    const stationNumber = ACTIVE_STATION_COUNT + i + 1;
    const stationId = `station-${stationNumber.toString().padStart(2, '0')}`;
    const sliceStart = memberOffset + i * MEMBERS_PER_STATION;
    const membersForStation = demoMemberProfiles.slice(
      sliceStart,
      sliceStart + MEMBERS_PER_STATION,
    );
    if (membersForStation.length < MEMBERS_PER_STATION) break;
    const plans: DemoMemberPlan[] = membersForStation.map(
      (profile, planIdx) => {
        const exercise =
          demoExercises[(stationNumber + planIdx) % demoExercises.length];
        const tempoProfile =
          tempoCycle[(stationNumber + planIdx) % tempoCycle.length];
        const baseWeight = 55 + ((stationNumber * 7 + planIdx * 11) % 90);
        const repFloor = clampReps(8 + ((stationNumber + planIdx) % 3));
        const repCeil = clampReps(repFloor + 2);
        const concentricFloor = 0.95 + ((stationNumber + planIdx) % 4) * 0.12;
        const eccentricFloor = 1.35 + ((stationNumber + planIdx) % 4) * 0.18;
        return {
          memberId: profile.id,
          exerciseId: exercise.id,
          baseWeight,
          weightVariance: 8 + ((stationNumber + planIdx) % 5) * 2,
          repTargetRange: [repFloor, repCeil],
          tempoProfile,
          concentricRange: [
            Number(concentricFloor.toFixed(2)),
            Number((concentricFloor + 0.65).toFixed(2)),
          ],
          eccentricRange: [
            Number(eccentricFloor.toFixed(2)),
            Number((eccentricFloor + 0.9).toFixed(2)),
          ],
        };
      },
    );
    staticStations.push({
      stationId,
      stationNumber,
      status: statusCycle[i % statusCycle.length],
      plans,
    });
  }

  return staticStations;
};

const staticStationPlans = createStaticStationPlans();

const allStationPlans: DemoStationPlan[] = [
  ...activeStationPlans,
  ...staticStationPlans,
];

const demoGym: Gym = {
  id: DEMO_GYM_ID,
  name: 'Motivision Demo Lab',
  address: '123 Demo Way',
  stationIds: allStationPlans.map((station) => station.stationId),
  memberIds: demoMemberProfiles.map((profile) => profile.id),
  trainerIds: ['demo-trainer'],
  managerIds: ['demo-manager'],
  logoUrl: '',
  blackLogoUrl: '',
  scheduleMeetingLink: '',
  configuration: {
    enabledFeatures: {
      enableDashboardLiveWorkouts: true,
    },
  },
};

const demoGroup: Group = {
  id: DEMO_GROUP_ID,
  name: 'Demo Athletes',
  memberIds: demoMemberProfiles.map((profile) => profile.id),
  trainerIds: ['demo-trainer'],
  managerIds: ['demo-manager'],
};

const demoGroups: Record<string, Group> = {
  [demoGroup.id]: demoGroup,
};

const randomBetween = (min: number, max: number) =>
  Number((Math.random() * (max - min) + min).toFixed(2));

const randomInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const createTempoValue = (
  min: number,
  max: number,
  profile: TempoProfile,
): number => {
  const base = randomBetween(min, max);
  if (profile === 'fast' && Math.random() < 0.65) {
    return Number((min * randomBetween(0.6, 0.95)).toFixed(2));
  }
  if (profile === 'slow' && Math.random() < 0.65) {
    return Number((max * randomBetween(1.05, 1.35)).toFixed(2));
  }
  if (profile === 'balanced' && Math.random() < 0.25) {
    const biasSlow = Math.random() > 0.5;
    return biasSlow
      ? Number((max * randomBetween(1.03, 1.2)).toFixed(2))
      : Number((min * randomBetween(0.8, 0.97)).toFixed(2));
  }
  return base;
};

const buildSessionMeta = (
  station: DemoStationPlan,
  plan: DemoMemberPlan,
  reps: number,
  weight: number,
): SessionMeta => ({
  exerciseId: plan.exerciseId,
  memberId: plan.memberId,
  weight,
  reps,
  metrics: {
    concentric: {
      min: plan.concentricRange[0],
      max: plan.concentricRange[1],
      type: AutomaticMetricsType.Tempo,
    },
    eccentric: {
      min: plan.eccentricRange[0],
      max: plan.eccentricRange[1],
      type: AutomaticMetricsType.Tempo,
    },
  },
  activeMembers: station.plans.map((p) => p.memberId),
  stationNumber: station.stationNumber,
});

const buildRepDurations = (
  meta: SessionMeta,
  profile: TempoProfile,
): { upDuration: number; downDuration: number } => ({
  upDuration: createTempoValue(
    meta.metrics.concentric.min,
    meta.metrics.concentric.max,
    profile,
  ),
  downDuration: createTempoValue(
    meta.metrics.eccentric.min,
    meta.metrics.eccentric.max,
    profile,
  ),
});

const createRepMetric = (
  meta: SessionMeta,
  profile: TempoProfile,
): RepMetric => {
  const { upDuration, downDuration } = buildRepDurations(meta, profile);
  return {
    upDuration,
    downDuration,
    upVelocity: Number((1 / Math.max(upDuration, 0.01)).toFixed(2)),
    downVelocity: Number((1 / Math.max(downDuration, 0.01)).toFixed(2)),
  };
};

const createRepFrame = (
  meta: SessionMeta,
  rep: number,
  profile: TempoProfile,
): LiveRepFrame => {
  const { upDuration, downDuration } = buildRepDurations(meta, profile);
  return {
    rackNumber: meta.stationNumber,
    rep,
    up: true,
    down: true,
    upDuration,
    downDuration,
    memberId: meta.memberId,
    exerciseId: meta.exerciseId,
    preset: {
      performedSets: 1,
      reps: meta.reps,
      weight: meta.weight,
      minConcentric: meta.metrics.concentric.min,
      maxConcentric: meta.metrics.concentric.max,
      minEccentric: meta.metrics.eccentric.min,
      maxEccentric: meta.metrics.eccentric.max,
      concentricType: meta.metrics.concentric.type,
      eccentricType: meta.metrics.eccentric.type,
    },
  };
};

const createRuntime = (
  station: DemoStationPlan,
  planIndex: number,
): StationRuntime => {
  const plan = station.plans[planIndex];
  const targetReps = clampReps(
    randomInt(plan.repTargetRange[0], plan.repTargetRange[1]),
  );
  const weightVariance = randomBetween(
    -plan.weightVariance,
    plan.weightVariance,
  );
  const weight = Math.round(Math.max(35, plan.baseWeight + weightVariance));
  const meta = buildSessionMeta(station, plan, targetReps, weight);
  const sessionId = `demo-${station.stationId}-${plan.memberId}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;
  const initialFrames = Array.from({ length: Math.min(3, targetReps) }).map(
    (_, index) => createRepFrame(meta, index + 1, plan.tempoProfile),
  );
  return {
    config: station,
    currentPlanIndex: planIndex,
    sessionId,
    meta,
    frames: initialFrames,
    repsCompleted: initialFrames.length,
    targetReps,
    lastTs: Date.now(),
  };
};

const seedStationStates = (): Record<string, StationRuntime> =>
  activeStationPlans.reduce<Record<string, StationRuntime>>((acc, station) => {
    acc[station.stationId] = createRuntime(station, 0);
    return acc;
  }, {});

const advanceStationStates = (
  prev: Record<string, StationRuntime>,
): Record<string, StationRuntime> => {
  const next: Record<string, StationRuntime> = {};
  Object.values(prev).forEach((runtime) => {
    const plan = runtime.config.plans[runtime.currentPlanIndex];
    let frames = runtime.frames;
    let repsCompleted = runtime.repsCompleted;
    const withinTarget = runtime.repsCompleted < runtime.targetReps;
    if (withinTarget) {
      const nextRepNumber = runtime.repsCompleted + 1;
      const newFrame = createRepFrame(
        runtime.meta,
        nextRepNumber,
        plan.tempoProfile,
      );
      frames = [...runtime.frames, newFrame];
      repsCompleted = nextRepNumber;
    }
    const shouldRotate =
      repsCompleted >= runtime.targetReps ||
      (repsCompleted > Math.max(3, runtime.targetReps - 2) &&
        Math.random() < 0.2);
    if (shouldRotate) {
      const nextPlanIndex =
        (runtime.currentPlanIndex + 1) % runtime.config.plans.length;
      next[runtime.config.stationId] = createRuntime(
        runtime.config,
        nextPlanIndex,
      );
    } else {
      next[runtime.config.stationId] = {
        ...runtime,
        frames,
        repsCompleted,
        lastTs: Date.now(),
      };
    }
  });
  return next;
};

const createHistoricalSessions = (): Record<string, TrainingRoutineSession> => {
  const sessions: Record<string, TrainingRoutineSession> = {};
  allStationPlans.forEach((station) => {
    station.plans.forEach((plan) => {
      if (sessions[plan.memberId]) return;
      const reps = clampReps(
        randomInt(plan.repTargetRange[0], plan.repTargetRange[1]),
      );
      const weightVariance = randomBetween(
        -plan.weightVariance,
        plan.weightVariance,
      );
      const weight = Math.round(Math.max(25, plan.baseWeight + weightVariance));
      const meta = buildSessionMeta(station, plan, reps, weight);
      const analyzedMetricsEntries: Record<string, RepMetric> = {};
      for (let rep = 1; rep <= reps; rep++) {
        analyzedMetricsEntries[String(rep)] = createRepMetric(
          meta,
          plan.tempoProfile,
        );
      }
      sessions[plan.memberId] = {
        name: 'Demo Showcase Session',
        id: `demo-history-${plan.memberId}`,
        memberId: plan.memberId,
        status: Status.Complete,
        routine: {
          name: 'Demo Showcase Session',
          description: 'Scripted demo workout',
          exerciseGroups: [],
        },
        performedRoutine: {
          name: 'Demo Showcase Session',
          performedDuration: 900,
          exerciseExertionRates: [],
          currentExerciseGroupIndex: 0,
          currentSetGroupIndex: 0,
          currentSetIndex: 0,
          performedExerciseGroups: [
            {
              name: 'Demo Block',
              trainerNotes: [],
              sets: [
                [
                  {
                    exerciseId: plan.exerciseId,
                    rest: 60,
                    reps,
                    weight,
                    oneRMPercentage: null,
                    duration: null,
                    metrics: meta.metrics,
                    forcedManual: false,
                    startedTimestamp: Date.now() - 900000,
                    finishedTimestamp: Date.now() - 870000,
                    performedRest: 60,
                    performedWeight: weight,
                    performedReps: clampReps(reps - randomInt(0, 2)),
                    performedDuration: null,
                    performedRangeSuccess: null,
                    analyzedMetrics: [analyzedMetricsEntries],
                    status: Status.Complete,
                    stationId: station.stationId,
                    stationNumber: station.stationNumber,
                  },
                ],
              ],
            },
          ],
        },
        createdDate: new Date().toISOString(),
      };
    });
  });
  return sessions;
};

const demoHistoricalSessions = createHistoricalSessions();

interface MemberSnapshot {
  meta: SessionMeta;
  frames: LiveRepFrame[];
}

const generateMemberSnapshot = (
  station: DemoStationPlan,
  plan: DemoMemberPlan,
): MemberSnapshot => {
  const reps = clampReps(
    randomInt(plan.repTargetRange[0], plan.repTargetRange[1]),
  );
  const weightVariance = randomBetween(
    -plan.weightVariance,
    plan.weightVariance,
  );
  const weight = Math.round(Math.max(35, plan.baseWeight + weightVariance));
  const meta = buildSessionMeta(station, plan, reps, weight);
  const frames = Array.from({ length: reps }).map((_, repIndex) =>
    createRepFrame(meta, repIndex + 1, plan.tempoProfile),
  );
  return { meta, frames };
};

const presenceSnapshots: Record<string, Record<string, LiveRepFrame[]>> =
  allStationPlans.reduce<Record<string, Record<string, LiveRepFrame[]>>>(
    (acc, station) => {
      const memberMap: Record<string, LiveRepFrame[]> = {};
      station.plans.forEach((plan) => {
        memberMap[plan.memberId] = generateMemberSnapshot(
          station,
          plan,
        ).frames;
      });
      acc[station.stationId] = memberMap;
      return acc;
    },
    {},
  );

const createStaticSnapshots = () => {
  const snapshots: Record<string, MemberSnapshot[]> = {};
  staticStationPlans.forEach((station) => {
    snapshots[station.stationId] = station.plans.map((plan) =>
      generateMemberSnapshot(station, plan),
    );
  });
  return snapshots;
};

const staticSnapshots = createStaticSnapshots();

const LiveDemo: React.FC = () => {
  const baseSessionData = useSessionData();
  const baseUserAction = useUserAction();
  const [stationStates, setStationStates] =
    useState<Record<string, StationRuntime>>(seedStationStates);
  const [staticCycle, setStaticCycle] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    staticStationPlans.forEach((station) => {
      initial[station.stationId] = 0;
    });
    return initial;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setStationStates((prev) => advanceStationStates(prev));
    }, 2300);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!staticStationPlans.length) return;
    const interval = setInterval(() => {
      setStaticCycle((prev) => {
        const next = { ...prev };
        staticStationPlans.forEach((station) => {
          const length = station.plans.length;
          if (length === 0) return;
          next[station.stationId] =
            ((prev[station.stationId] ?? 0) + 1) % length;
        });
        return next;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const liveSessionsByStation = useMemo<
    Record<string, LiveSessionState | undefined>
  >(() => {
    const sessions: Record<string, LiveSessionState | undefined> = {};
    Object.values(stationStates).forEach((runtime) => {
      sessions[runtime.config.stationId] = {
        stationId: runtime.config.stationId,
        sessionId: runtime.sessionId,
        meta: runtime.meta,
        repFrames: runtime.frames,
        lastTs: runtime.lastTs,
        completed: runtime.repsCompleted >= runtime.targetReps,
      };
    });
    staticStationPlans.forEach((station) => {
      const cycleIndex = staticCycle[station.stationId] ?? 0;
      const snapshots = staticSnapshots[station.stationId];
      const snapshot = snapshots?.[cycleIndex];
      if (!snapshot) return;
      sessions[station.stationId] = {
        stationId: station.stationId,
        sessionId: `static-${station.stationId}-${cycleIndex}`,
        meta: snapshot.meta,
        repFrames: snapshot.frames,
        lastTs: Date.now(),
        completed: true,
      };
    });
    return sessions;
  }, [stationStates, staticCycle]);

  const presenceByStation = useMemo<Record<string, StationPresence>>(() => {
    const presence: Record<string, StationPresence> = {};
    allStationPlans.forEach((station) => {
      const runtime = stationStates[station.stationId];
      const activePlan =
        runtime?.config.plans[runtime.currentPlanIndex] ?? station.plans[0];
      presence[station.stationId] = {
        rtServiceOnline: true,
        status: station.status,
        tabletOnline: true,
        currentMember: runtime ? runtime.meta.memberId : activePlan.memberId,
        activeMembers: station.plans.map((plan) => plan.memberId),
        stationNumber: station.stationNumber,
        details: {
          demoSnapshots: presenceSnapshots[station.stationId],
        },
      };
    });
    return presence;
  }, [stationStates]);

  const gatewayValue = useMemo<GatewayContextValue>(
    () => ({
      status: 'connected',
      ready: undefined,
      presenceByStation,
      liveSessionsByStation,
      errors: [],
      connect: () => {},
      disconnect: () => {},
      updateSubscriptions: () => {},
    }),
    [liveSessionsByStation, presenceByStation],
  );

  const sessionDataValue = useMemo<SessionDataContextProps>(
    () => ({
      ...baseSessionData,
      gym: demoGym,
      members: demoMembers,
      groups: demoGroups,
      mergedExercises: demoExercises,
      mergedExercisesMap: demoExercisesMap,
    }),
    [baseSessionData],
  );

  const userActionValue = useMemo<UserActionContextProps>(
    () => ({
      ...baseUserAction,
      getLastSessions: async (memberIds: string[]) => {
        if (!memberIds?.length) return null;
        const result: Record<string, TrainingRoutineSession> = {};
        memberIds.forEach((id) => {
          if (demoHistoricalSessions[id]) {
            result[id] = demoHistoricalSessions[id];
          }
        });
        return Object.keys(result).length ? result : null;
      },
    }),
    [baseUserAction],
  );

  return (
    <GatewayContext.Provider value={gatewayValue}>
      <SessionDataContext.Provider value={sessionDataValue}>
        <UserActionContext.Provider value={userActionValue}>
          <LiveWorkouts />
        </UserActionContext.Provider>
      </SessionDataContext.Provider>
    </GatewayContext.Provider>
  );
};

export default LiveDemo;
